
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Zap, Settings, Trophy, DollarSign, BarChart3, Target, User, Home, LogOut, Star, HelpCircle, CreditCard, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/use-toast";
import RestrictedAccessModal from "./RestrictedAccessModal";

const MobileNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [restrictedModal, setRestrictedModal] = useState({ open: false, featureName: '' });
  const [isOpen, setIsOpen] = useState(false);
  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.clear();
      sessionStorage.clear();
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso.",
      });
      setIsOpen(false);
      window.location.href = "/";
    } catch (error) {
      console.error("Error during logout:", error);
      localStorage.clear();
      sessionStorage.clear();
      toast({
        title: "Desconectado",
        description: "Você foi desconectado.",
        variant: "destructive",
      });
      setIsOpen(false);
      window.location.href = "/";
    }
  };

  const handleNavigation = (path: string) => {
    setIsOpen(false);
    try {
      navigate(path);
    } catch (error) {
      console.error("Navigation error:", error);
      window.location.href = path;
    }
  };

  const handleRestrictedAccess = (featureName: string, path: string) => {
    if (isAuthenticated) {
      handleNavigation(path);
    } else {
      setIsOpen(false);
      setRestrictedModal({ open: true, featureName });
    }
  };

  // Menu para visitantes (não logados) - agora mostra todos os itens
  const VisitorMenu = () => (
    <div className="flex flex-col space-y-4 mt-8">
      {/* Botão em destaque para demo */}
      <div className="mb-6">
        <Button 
          onClick={() => handleRestrictedAccess("Gerar Palpites com IA", "/gerar-jogos")}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-lg py-6 rounded-lg shadow-lg"
        >
          🎯 Gerar Palpites com IA
        </Button>
      </div>

      {/* Links principais */}
      <Button 
        onClick={() => handleNavigation("/login")}
        variant="ghost" 
        className="w-full justify-start text-lg py-3"
      >
        Entrar
      </Button>
      
      <Button 
        onClick={() => handleNavigation("/cadastro")}
        variant="ghost" 
        className="w-full justify-start text-lg py-3"
      >
        Cadastrar
      </Button>

      <Button
        onClick={() => handleNavigation("/estatisticas")}
        variant="ghost"
        className="w-full justify-start estatisticas-destaque"
      >
        <BarChart3 className="mr-2 h-4 w-4" />
        Estatísticas
      </Button>

      <Button
        onClick={() => handleNavigation("/pagamento")}
        variant="ghost"
        className="w-full justify-start"
      >
        <CreditCard className="mr-2 h-4 w-4" />
        Pagamento
      </Button>

      {/* Seção Jogos IA - visitante vê tudo mas não acessa */}
      <div className="space-y-2">
        <div className="px-3 py-2 text-sm font-medium text-muted-foreground">Jogos IA</div>
        <Button 
          onClick={() => handleRestrictedAccess("Gerar Jogos IA", "/gerar-jogos")}
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          <Zap className="mr-2 h-4 w-4" />
          Gerar Jogos IA
        </Button>
        <Button 
          onClick={() => handleRestrictedAccess("Puxadas do Bicho", "/puxadas")}
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          <Target className="mr-2 h-4 w-4" />
          Puxadas do Bicho
        </Button>
        <Button 
          onClick={() => handleRestrictedAccess("Loterias Caixa", "/loterias")}
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          <Trophy className="mr-2 h-4 w-4" />
          Loterias Caixa
        </Button>
        <Button 
          onClick={() => handleRestrictedAccess("Software Turbinado", "/software")}
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          <Settings className="mr-2 h-4 w-4" />
          Software Turbinado
        </Button>
        <Button 
          onClick={() => handleRestrictedAccess("Métodos IA", "/metodos")} 
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          🎯
          Métodos IA
        </Button>
        <Button 
          onClick={() => handleRestrictedAccess("Soma Chave de Ouro", "/metodos?method=soma-chave-ouro")}
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          🔑
          Soma Chave de Ouro
        </Button>
        <Button 
          onClick={() => handleRestrictedAccess("Calculadora Chave de Ouro", "/metodos?method=calculadora-chave-ouro")}
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          🧮
          Calculadora Chave de Ouro
        </Button>
      </div>

      {/* Seção Resultados - Resultado RIO é público, demais são restritos */}
      <div className="space-y-2">
        <div className="px-3 py-2 text-sm font-medium text-muted-foreground">Resultados</div>
        <Button 
          onClick={() => handleNavigation("/resultados-bicho")}
          variant="ghost" 
          className="w-full justify-start pl-6"
        >
          <BarChart3 className="mr-2 h-4 w-4" />
          Resultado RIO
        </Button>
      </div>

      <Button 
        onClick={() => handleNavigation("/loja")}
        variant="ghost" 
        className="w-full justify-start loja-vibrante"
      >
        <ShoppingBag className="mr-2 h-4 w-4" />
        Loja/Produtos
      </Button>

      <Button 
        onClick={() => handleNavigation("/planos")}
        variant="ghost" 
        className="w-full justify-start"
      >
        <CreditCard className="mr-2 h-4 w-4" />
        Planos/Pagamentos
      </Button>

      <div className="border-t pt-4 space-y-2">
        <Button 
          onClick={() => handleNavigation("/")}
          variant="ghost" 
          className="w-full justify-start"
        >
          Sobre / Como funciona
        </Button>

        <Button 
          onClick={() => handleNavigation("/")}
          variant="ghost" 
          className="w-full justify-start"
        >
          <Star className="mr-2 h-4 w-4" />
          Depoimentos
        </Button>
      </div>
    </div>
  );

  const getUserDisplayName = () => {
    if (!user) return "";
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split("@")[0];
    return "Usuário";
  };

  // Menu para usuários logados
  const LoggedInMenu = () => (
    <div className="flex flex-col space-y-4 mt-8">
      {/* User Info */}
      <div className="flex items-center gap-3 p-4 bg-primary/10 rounded-lg border border-primary/20">
        <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
          {getUserDisplayName().substring(0, 2).toUpperCase()}
        </div>
        <div>
          <div className="font-semibold text-foreground">Olá, {getUserDisplayName()}!</div>
          <div className="text-xs text-muted-foreground">{user?.email}</div>
        </div>
      </div>
      <Button 
        onClick={() => handleNavigation("/dashboard")}
        variant={isActive("/dashboard") ? "secondary" : "ghost"} 
        className="w-full justify-start"
      >
        <Home className="mr-2 h-4 w-4" />
        Dashboard
      </Button>
      
      <div className="space-y-2">
        <div className="px-3 py-2 text-sm font-medium text-muted-foreground">Jogos IA</div>
        <Button 
          onClick={() => handleNavigation("/gerar-jogos")}
          variant={isActive("/gerar-jogos") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          <Zap className="mr-2 h-4 w-4" />
          Gerar Jogos IA
        </Button>
        <Button 
          onClick={() => handleNavigation("/puxadas")}
          variant={isActive("/puxadas") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          <Target className="mr-2 h-4 w-4" />
          Puxadas do Bicho
        </Button>
        <Button 
          onClick={() => handleNavigation("/loterias")}
          variant={isActive("/loterias") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          <Trophy className="mr-2 h-4 w-4" />
          Loterias Caixa
        </Button>
        <Button 
          onClick={() => handleNavigation("/software")}
          variant={isActive("/software") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          <Settings className="mr-2 h-4 w-4" />
          Software Turbinado
        </Button>
        <Button 
          onClick={() => handleNavigation("/metodos")}
          variant={isActive("/metodos") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          🎯
          Métodos IA
        </Button>
        <Button 
          onClick={() => handleNavigation("/metodos?method=soma-chave-ouro")}
          variant={isActive("/metodos?method=soma-chave-ouro") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          🔑
          Soma Chave de Ouro
        </Button>
      </div>

      <div className="space-y-2">
        <div className="px-3 py-2 text-sm font-medium text-muted-foreground">Resultados</div>
        <Button 
          onClick={() => handleNavigation("/resultados-bicho")}
          variant={isActive("/resultados-bicho") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          <BarChart3 className="mr-2 h-4 w-4" />
          Resultado RIO
        </Button>
        <Button 
          onClick={() => handleNavigation("/estatisticas")}
          variant={isActive("/estatisticas") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6 estatisticas-destaque"
        >
          <BarChart3 className="mr-2 h-4 w-4" />
          Estatísticas
        </Button>
      </div>

      <Button 
        onClick={() => handleNavigation("/loja")}
        variant="ghost" 
        className="w-full justify-start loja-vibrante"
      >
        <ShoppingBag className="mr-2 h-4 w-4" />
        Loja/Produtos
      </Button>

      <Button 
        onClick={() => handleNavigation("/pagamento")}
        variant={isActive("/pagamento") ? "secondary" : "ghost"} 
        className="w-full justify-start"
      >
        💳 Pagamento PIX
      </Button>

      <Button 
        onClick={() => handleNavigation("/planos")}
        variant={isActive("/planos") ? "secondary" : "ghost"} 
        className="w-full justify-start"
      >
        <CreditCard className="mr-2 h-4 w-4" />
        Planos/Pagamentos
      </Button>

      <div className="space-y-2">
        <div className="px-3 py-2 text-sm font-medium text-muted-foreground">Conta</div>
        <Button 
          onClick={() => handleNavigation("/afiliados")}
          variant={isActive("/afiliados") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          <DollarSign className="mr-2 h-4 w-4" />
          Programa de Afiliados
        </Button>
        <Button 
          onClick={() => handleNavigation("/minha-conta")}
          variant={isActive("/minha-conta") ? "secondary" : "ghost"} 
          className="w-full justify-start pl-6"
        >
          <User className="mr-2 h-4 w-4" />
          Minha Conta
        </Button>
        <Button 
          onClick={handleLogout}
          variant="ghost" 
          className="w-full justify-start pl-6 text-red-600 hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sair
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden">
            <Menu className="h-5 w-5" />
            <span className="sr-only">Abrir menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[320px] overflow-y-auto">
          <div className="flex flex-col h-full">
            {/* Header do menu */}
            <div className="flex items-center gap-2 pb-6 border-b">
              <h2 className="text-xl font-bold text-primary">Palpite Guru</h2>
            </div>
            
            {/* Conteúdo baseado no estado do usuário */}
            {isAuthenticated ? <LoggedInMenu /> : <VisitorMenu />}
          </div>
        </SheetContent>
      </Sheet>

      <RestrictedAccessModal
        open={restrictedModal.open}
        onClose={() => setRestrictedModal({ open: false, featureName: '' })}
        featureName={restrictedModal.featureName}
      />
    </>
  );
};

export default MobileNavigation;
